import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createStackNavigator } from "@react-navigation/stack";
import * as SplashScreen from "expo-splash-screen";
import * as Segment from "./analytics";
import { useQuery } from "@apollo/client";

import { NavigatorParamList } from "./navigators/types";
import { CURRENT_CUSTOMER } from "./screens/queries";
import { commonHeader } from "./navigators/AuthNavigator";
import { ScreenHeaderTitle } from "./components/ScreenHeader";
import GuestVisitFlow from "./navigators/GuestVisitFlow";
import NewVisitFlow from "./navigators/NewVisitFlow";
import AppointmentBookingFlow from "./navigators/AppointmentBookingFlow";
import WelcomeGuestScreen from "./screens/WelcomeGuestScreen";
import CustomerDashboardWeb from "./screens/CustomerDashboardWeb/CustomerDashboardWeb";
import ExistingVisitFlow from "./navigators/ExistingVisitFlow";
import ProfileNavigator from "./navigators/ProfileNavigator";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";
import VisitScreen from "./screens/VisitScreen";

import { RootState } from "./state";
import { colors } from "./theme";
import { useFirebaseAuth } from "./firebase/AuthProvider";

const MainStack = createStackNavigator<NavigatorParamList>();

const ScheduledNewVisitFlow = (props: any) => (
  <NewVisitFlow {...props} isUnscheduled={false} />
);

const UnscheduledNewVisitFlow = (props: any) => (
  <NewVisitFlow {...props} isUnscheduled={true} />
);

const NavigationWeb = () => {
  const [didLoad, setDidLoad] = useState(false);
  const token = useSelector((store: RootState) => store.auth.token);
  const {
    firebase: firebaseUser,
    currentCustomer: firebaseCustomer,
  } = useFirebaseAuth();
  const hasAuthentication = !!firebaseUser || !!token;
  const { data, loading } = useQuery(CURRENT_CUSTOMER, {
    skip: !hasAuthentication,
    fetchPolicy: "network-only",
  });

  const currentCustomer = data?.viewer || firebaseCustomer;

  useEffect(() => {
    if (currentCustomer) {
      Segment.identifyWithTraits(currentCustomer.id, {
        name: currentCustomer.name,
        email: currentCustomer.emailAddress,
      });
    }
  }, [currentCustomer]);

  useEffect(() => {
    // If we already loaded, don't perform this step again
    if (didLoad) return;

    if ((!loading && data) || (!loading && !data)) {
      setTimeout(() => {
        setDidLoad(true);
        SplashScreen.hideAsync();
      }, 400);
    }
  }, [data, loading]);

  // If we already loaded the app once, we don't want to show this empty state
  // again to avoid some really janky re-renders of screens
  if (loading && !didLoad) {
    return null;
  }

  return (
    <MainStack.Navigator
      screenOptions={{
        headerTitleAlign: "center",
        cardStyle: { backgroundColor: colors.black },
      }}
    >
      <MainStack.Screen
        name="Main"
        options={{
          headerShown: false,
          title: currentCustomer ? "My Visits" : "Welcome",
        }}
      >
        {(props) => {
          const showDashboard =
            !!currentCustomer || !!props.route.params?.showDashboard;

          return showDashboard ? (
            <CustomerDashboardWeb {...props} />
          ) : (
            <WelcomeGuestScreen {...props} />
          );
        }}
      </MainStack.Screen>
      <MainStack.Screen
        name="NewVisitFlow"
        component={ScheduledNewVisitFlow}
        options={{ headerShown: false, title: "New Visit" }}
      />
      <MainStack.Screen
        name="NewVisitUnscheduledFlow"
        component={UnscheduledNewVisitFlow}
        options={{ headerShown: false, title: "New Visit" }}
      />
      <MainStack.Screen
        name="ExistingVisitFlow"
        component={ExistingVisitFlow}
        options={{ headerShown: false }}
      />
      <MainStack.Screen
        name="AppointmentBookingFlow"
        component={AppointmentBookingFlow}
        options={{ headerShown: false, title: "Book an Appointment" }}
      />
      <MainStack.Screen
        name="ViewProfile"
        component={ProfileNavigator}
        options={{ headerShown: false }}
      />
      <MainStack.Screen
        name="Visit"
        component={VisitScreen}
        options={
          {
            ...commonHeader,
            title: "Visit Details",
            headerTitle: (props: any) => <ScreenHeaderTitle {...props} />,
          } as any
        }
      />
      <MainStack.Screen
        name="ResetPassword"
        component={ResetPasswordScreen}
        options={
          {
            ...commonHeader,
            title: "Reset Password",
            headerTitle: (props: any) => <ScreenHeaderTitle {...props} />,
          } as any
        }
        initialParams={{ isGuestFlow: true }}
      />
    </MainStack.Navigator>
  );
};

export default NavigationWeb;
